import { defineStore } from 'pinia'

export type HighFrequencyGameSlip = {
  isQuickPick: boolean
  selectedAddOns: string[] | undefined
  selectedNumbers: HighFrequencySelectedNumberField[] | undefined
  selectedQuantity: number | undefined
  selectedWager: string | undefined
}

export type HighFrequencySelectedNumberField = {
  name: string
  numbers: number[]
}

type State = HighFrequencyGameSlip | undefined

export const useHighFrequencyGameStore = (id: string) =>
  defineStore(
    `ltc_high_frequency_${id.toLowerCase()}`,
    () => {
      const addEmptyState = () => ({
        isQuickPick: false,
        selectedAddOns: [],
        selectedNumbers: [],
        selectedQuantity: 5,
        selectedWager: undefined,
      })

      const state = ref<State>(addEmptyState())

      const isQuickPick = computed(() => state.value?.isQuickPick ?? false)

      const selectedAddOns = computed(() => state.value?.selectedAddOns ?? [])
      const setAddOn = (name: string, isActive: boolean) => {
        if (!state.value) return
        if (!state.value?.selectedAddOns) {
          state.value.selectedAddOns = []
        }

        if (!isActive) {
          state.value.selectedAddOns = state.value.selectedAddOns.filter((_name) => _name !== name)
          return
        }

        if (isActive && !state.value.selectedAddOns.includes(name)) {
          state.value.selectedAddOns = [...state.value.selectedAddOns, name]
        }
      }

      const setIsQuickPick = (isQuickPick: boolean) => {
        if (!state.value) return
        state.value.isQuickPick = isQuickPick
      }

      const selectedNumbers = computed(() => state.value?.selectedNumbers ?? [])
      const setNumbers = (entry: HighFrequencySelectedNumberField) => {
        if (!state.value) return

        if (!state.value.selectedNumbers) {
          state.value.selectedNumbers = [entry]
        } else {
          const fieldIndex = state.value.selectedNumbers.findIndex(
            ({ name }) => name === entry.name,
          )

          if (fieldIndex >= 0 && fieldIndex < state.value.selectedNumbers.length) {
            state.value.selectedNumbers[fieldIndex] = entry
          } else {
            state.value.selectedNumbers.push(entry)
          }
        }
      }

      const selectedQuantity = computed(() => state.value?.selectedQuantity)
      const setQuantity = (quantity: number) => {
        if (!state.value) return

        state.value.selectedQuantity = quantity
      }

      const selectedWager = computed(() => state.value?.selectedWager)
      const setWager = (wagerId: string) => {
        if (!state.value) return

        state.value.selectedWager = wagerId
      }

      const reset = () => {
        if (!state.value) return

        state.value = addEmptyState()
      }

      return {
        isQuickPick,
        reset,
        selectedAddOns,
        selectedNumbers,
        selectedQuantity,
        selectedWager,
        setAddOn,
        setIsQuickPick,
        setNumbers,
        setQuantity,
        setWager,
        state,
      }
    },
    {
      persist: {
        storage: piniaPluginPersistedstate.cookies({
          sameSite: 'strict',
        }),
      },
    },
  )()
